import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

export type SiteLanguage = 'en' | 'es' | 'fr' | 'de' | 'zh-CN' | 'ha' | 'ig' | 'yo';

interface GoogleTranslateInstance {
  TranslateElement: new (options: Record<string, unknown>, elementId: string) => unknown;
  TranslateElementInit?: { InlineLayout?: { SIMPLE: number } };
}

declare global {
  interface Window {
    google?: { translate?: GoogleTranslateInstance };
    googleTranslateElementInit?: () => void;
  }
}

interface LanguageContextValue {
  language: SiteLanguage;
  setLanguage: (language: SiteLanguage) => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);
const GOOGLE_TRANSLATE_SCRIPT_ID = 'a3-google-translate-script';
const TRANSLATABLE_LANGUAGES: SiteLanguage[] = ['en', 'es', 'fr', 'de', 'zh-CN', 'ha', 'ig', 'yo'];

// Google Translate replaces text nodes outside React's reconciliation tree.
// When React later removes the original translated node, the parent no longer
// contains it and the native removeChild throws NotFoundError. Install this
// before React mounts, and only ignore that already-removed-node case while a
// Google Translate language cookie is active. All valid DOM removals continue
// to use the native implementation.
const installTranslatedNodeRemovalGuard = () => {
  if (typeof Node === 'undefined') return;
  const nodePrototype = Node.prototype as Node & { __a3TranslateRemoveChildGuard?: boolean };
  if (nodePrototype.__a3TranslateRemoveChildGuard) return;

  const nativeRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    const googleTranslationActive = typeof document !== 'undefined' &&
      document.cookie.split(';').some((cookie) => /^\s*googtrans=\/en\/(?!en(?:;|$))/.test(cookie));
    if (googleTranslationActive && child.parentNode !== this) return child;
    return nativeRemoveChild.call(this, child) as T;
  };
  nodePrototype.__a3TranslateRemoveChildGuard = true;
};

installTranslatedNodeRemovalGuard();

const isSiteLanguage = (value: string | null): value is SiteLanguage =>
  Boolean(value && TRANSLATABLE_LANGUAGES.includes(value as SiteLanguage));

const setTranslateCookie = (language: SiteLanguage) => {
  const value = language === 'en' ? '' : `/en/${language}`;
  const expires = language === 'en' ? 'expires=Thu, 01 Jan 1970 00:00:00 GMT;' : '';
  document.cookie = `googtrans=${value}; path=/; ${expires} SameSite=Lax`;
  const hostname = window.location.hostname;
  if (hostname.includes('.')) {
    document.cookie = `googtrans=${value}; path=/; domain=.${hostname.split('.').slice(-2).join('.')}; ${expires} SameSite=Lax`;
  }
};

const triggerGoogleTranslate = (language: SiteLanguage, attempts = 0) => {
  const selector = document.querySelector<HTMLSelectElement>('.goog-te-combo');
  if (selector) {
    selector.value = language;
    selector.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  if (attempts < 24) {
    window.setTimeout(() => triggerGoogleTranslate(language, attempts + 1), 250);
  }
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const languageRef = useRef<SiteLanguage>('en');
  const [language, setLanguageState] = useState<SiteLanguage>(() => {
    const savedLanguage = localStorage.getItem('language');
    const initial = savedLanguage === 'zh'
      ? 'zh-CN'
      : isSiteLanguage(savedLanguage) ? savedLanguage : 'en';
    languageRef.current = initial;
    return initial;
  });

  useEffect(() => {
    document.documentElement.lang = language;
    setTranslateCookie(language);

    const styleId = 'a3-google-translate-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = [
        '.goog-te-banner-frame.skiptranslate{display:none!important}',
        'body{top:0!important}',
        '.goog-tooltip,.goog-tooltip:hover{display:none!important}',
        '.goog-text-highlight{background-color:transparent!important;box-shadow:none!important}',
        '#google_translate_element{position:fixed!important;left:-10000px!important;top:0!important;width:1px!important;height:1px!important;overflow:hidden!important;pointer-events:none!important}',
      ].join('\n');
      document.head.appendChild(style);
    }

    window.googleTranslateElementInit = () => {
      const googleTranslate = window.google?.translate;
      if (!googleTranslate?.TranslateElement) return;
      const widget = document.getElementById('google_translate_element');
      if (!widget || widget.dataset.initialized === 'true') return;
      const simpleLayout = googleTranslate.TranslateElementInit?.InlineLayout?.SIMPLE;
      new googleTranslate.TranslateElement({
        pageLanguage: 'en',
        includedLanguages: TRANSLATABLE_LANGUAGES.filter((item) => item !== 'en').join(','),
        autoDisplay: false,
        layout: simpleLayout,
      }, 'google_translate_element');
      widget.dataset.initialized = 'true';
      if (languageRef.current !== 'en') triggerGoogleTranslate(languageRef.current);
    };

    if (!document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      script.onerror = () => console.error('Unable to load the website translation service.');
      document.head.appendChild(script);
    } else if (window.google?.translate?.TranslateElement) {
      window.googleTranslateElementInit();
    }
  }, [language]);

  const setLanguage = useCallback((nextLanguage: SiteLanguage) => {
    setLanguageState(nextLanguage);
    languageRef.current = nextLanguage;
    localStorage.setItem('language', nextLanguage);
    setTranslateCookie(nextLanguage);
    if (nextLanguage === 'en') {
      window.location.reload();
      return;
    }
    window.setTimeout(() => triggerGoogleTranslate(nextLanguage), 0);
  }, []);

  const contextValue = useMemo(() => ({ language, setLanguage }), [language, setLanguage]);

  return (
    <LanguageContext.Provider value={contextValue}>
      <div id="google_translate_element" aria-hidden="true" />
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within a LanguageProvider');
  return context;
};
